import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';

interface LanguageSelectorProps {
  className?: string;
  showText?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  showText = true 
}) => {
  const { currentLanguage, availableLanguages, changeLanguage, t, isLoading } = useLanguage();

  if (isLoading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <LanguageIcon className="h-5 w-5 text-gray-500" />
        {showText && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  }

  return (
    <Menu as="div" className={`relative inline-block text-left ${className}`}>
      <div>
        <Menu.Button className="flex items-center space-x-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
          <LanguageIcon className="h-4 w-4" />
          {showText && (
            <>
              <span>{currentLanguage?.native_name || t('select_language', 'general')}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </>
          )}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {availableLanguages.map((language) => (
            <Menu.Item key={language.id}>
              {({ active }) => (
                <button
                  onClick={() => changeLanguage(language.code)}
                  className={`${
                    active ? 'bg-gray-100' : ''
                  } ${
                    currentLanguage?.code === language.code ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                  } flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100`}
                >
                  <span className="mr-3 text-lg">{getFlagEmoji(language.code)}</span>
                  <div className="flex flex-col">
                    <span className="font-medium">{language.native_name}</span>
                    <span className="text-xs text-gray-500">{language.name}</span>
                  </div>
                  {currentLanguage?.code === language.code && (
                    <span className="ml-auto text-blue-600">✓</span>
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

// Helper function to get flag emojis
const getFlagEmoji = (languageCode: string): string => {
  const flagMap: Record<string, string> = {
    'hu': '🇭🇺',
    'en': '🇺🇸',
    'de': '🇩🇪',
    'fr': '🇫🇷',
    'es': '🇪🇸',
    'it': '🇮🇹',
    'pt': '🇵🇹',
    'ru': '🇷🇺',
    'zh': '🇨🇳',
    'ja': '🇯🇵',
    'ko': '🇰🇷',
  };
  return flagMap[languageCode] || '🌐';
};

export default LanguageSelector;